import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

/** /payment/success?session_id=... — polls /api/payments/status until paid. */
export function PaymentSuccess() {
  const { t, i18n } = useTranslation();
  const zh = !i18n.language?.startsWith("en");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sid = params.get("session_id");
  const [state, setState] = useState({ status: "checking", txn: null });

  useEffect(() => {
    if (!sid) { setState({ status: "error" }); return; }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ status: "paid", txn: data });
          setTimeout(() => {
            if (data.kind === "order") navigate(`/order-success/${data.ref_id}`);
            else if (data.kind === "booking") navigate(`/bookings/${data.ref_id}`);
          }, 900);
          return;
        }
        if (data.payment_status === "expired" || data.payment_status === "failed") {
          setState({ status: "failed", txn: data }); return;
        }
      } catch (e) { /* keep polling */ }
      if (!cancelled && attempts < 30) setTimeout(poll, 2000);
      else if (!cancelled) setState({ status: "timeout" });
    };
    poll();
    return () => { cancelled = true; };
  }, [sid, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#fdfbf7] px-6" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center">
        {state.status === "paid" ? (
          <>
            <CheckCircle2 size={48} className="text-[#00868e] mx-auto" />
            <h1 className="mt-6 font-serif-cn text-3xl text-[#003645] font-semibold" data-testid="payment-paid">
              {zh ? "支付成功" : "Payment successful"}
            </h1>
            <p className="mt-3 text-[#4a6e77] text-sm">
              {zh ? "正在跳转…" : "Redirecting…"}
            </p>
          </>
        ) : state.status === "failed" || state.status === "timeout" || state.status === "error" ? (
          <>
            <XCircle size={48} className="text-red-600 mx-auto" />
            <h1 className="mt-6 font-serif-cn text-3xl text-[#003645] font-semibold">
              {zh ? "支付未完成" : "Payment not completed"}
            </h1>
            <Link to="/dashboard" className="mt-8 inline-block text-[#005870] hover:text-[#00868e] font-medium">
              {zh ? "返回会员中心" : "Back to Dashboard"} →
            </Link>
          </>
        ) : (
          <>
            <Loader2 size={40} className="animate-spin text-[#005870] mx-auto" />
            <div className="mt-6 text-sm text-[#4a6e77]" data-testid="payment-checking">
              {zh ? "正在确认支付…" : "Confirming payment…"}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** /payment/cancel?kind=order&ref_id=... — order still payable; can retry or switch to bank-in. */
export function PaymentCancel() {
  const { t, i18n } = useTranslation();
  const zh = !i18n.language?.startsWith("en");
  const [params] = useSearchParams();
  const kind = params.get("kind");
  const ref_id = params.get("ref_id");

  const backTo = kind === "booking" && ref_id ? `/bookings/${ref_id}`
    : kind === "order" && ref_id ? `/orders/${ref_id}`
    : "/dashboard";

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#fdfbf7] px-6" data-testid="payment-cancel-page">
      <div className="max-w-md w-full text-center">
        <XCircle size={48} className="text-[#4a6e77] mx-auto" />
        <h1 className="mt-6 font-serif-cn text-3xl text-[#003645] font-semibold">
          {zh ? "已取消支付" : "Payment cancelled"}
        </h1>
        <p className="mt-3 text-[#4a6e77] text-sm">
          {zh ? "您的订单仍可支付，您可以重试或改用银行转账。" : "Your order is still payable — retry or switch to bank transfer."}
        </p>
        <Link to={backTo} className="mt-8 inline-block px-6 py-3 rounded-full bg-[#005870] text-white font-medium hover:bg-[#003645] transition-colors duration-200" data-testid="payment-cancel-back">
          {zh ? "返回订单" : "Back to order"}
        </Link>
      </div>
    </div>
  );
}
